import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import MainLayout from '../../components/layout/MainLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  MessageSquare, Send, Loader2, Search, ArrowLeft, Paperclip, X,
  FileText, Download, Package, CheckCircle2, Clock, AlertCircle,
  Archive, ArchiveRestore, FolderOpen, Wrench, Mic, ThumbsUp,
  ThumbsDown, Upload, Eye
} from 'lucide-react';

const ContractChatPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  
  // Deliverable state
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);
  const [deliverableFile, setDeliverableFile] = useState(null);
  const [deliverableComment, setDeliverableComment] = useState('');
  const [sendingDeliverable, setSendingDeliverable] = useState(false);
  const deliverableInputRef = useRef(null);
  
  // Validation state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [validationResponse, setValidationResponse] = useState('');
  const [validationNotes, setValidationNotes] = useState('');
  const [validating, setValidating] = useState(false);
  
  // Audio state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [showAudioOptions, setShowAudioOptions] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const isClient = user?.role === 'client';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Fetch task conversations for contract
  const fetchConversations = useCallback(async () => {
    if (!contractId) return;
    
    try {
      const res = await api.get(`/contracts/${contractId}/task-conversations`);
      setContract(res.data.contract);
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  }, [contractId]);
  
  // Fetch messages for selected task (using maintenance_tasks via contracts router)
  const fetchMessages = useCallback(async (taskId) => {
    if (!taskId) return;
    
    try {
      // Use contracts router endpoint for maintenance task messages
      const res = await api.get(`/contracts/tasks/${taskId}/messages`);
      setMessages(res.data || []);
      
      // Mark as read
      await api.post(`/contracts/tasks/${taskId}/mark-read`).catch(() => {});
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);
  
  // Load messages when task selected
  useEffect(() => {
    if (selectedTask) {
      fetchMessages(selectedTask.id);
    }
  }, [selectedTask, fetchMessages]);
  
  // Send message
  const handleSendMessage = async () => {
    if (!selectedTask || (!newMessage.trim() && !selectedFile)) return;
    
    setSending(true);
    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('message', newMessage);
        formData.append('is_deliverable', 'false');
        
        await api.post(`/contracts/tasks/${selectedTask.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post(`/contracts/tasks/${selectedTask.id}/messages`, {
          content: newMessage,
          type: 'message'
        });
      }
      
      setNewMessage('');
      setSelectedFile(null);
      fetchMessages(selectedTask.id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  // Send deliverable
  const handleSendDeliverable = async () => {
    if (!selectedTask || !deliverableFile) {
      toast.error('Veuillez joindre un fichier');
      return;
    }
    
    setSendingDeliverable(true);
    try {
      const formData = new FormData();
      formData.append('file', deliverableFile);
      formData.append('message', deliverableComment);
      formData.append('is_deliverable', 'true');
      
      await api.post(`/contracts/tasks/${selectedTask.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Livrable envoyé pour validation');
      setShowDeliverableModal(false);
      setDeliverableFile(null);
      setDeliverableComment('');
      fetchMessages(selectedTask.id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending deliverable:', error);
      toast.error('Erreur lors de l\'envoi du livrable');
    } finally {
      setSendingDeliverable(false);
    }
  };
  
  // Client validates version
  const handleValidateVersion = async () => {
    if (!selectedVersion || !validationResponse) return;
    
    setValidating(true);
    try {
      await api.post(`/contracts/tasks/${selectedTask.id}/versions/${selectedVersion.id}/respond`, {
        response: validationResponse,
        notes: validationNotes
      });
      
      toast.success(validationResponse === 'accepted' ? 'Livrable validé !' : 'Demande de modifications envoyée');
      setShowValidationModal(false);
      setSelectedVersion(null);
      setValidationResponse('');
      setValidationNotes('');
      fetchMessages(selectedTask.id);
      fetchConversations();
    } catch (error) {
      console.error('Error validating:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };
  
  // Archive conversation
  const handleArchive = async (taskId, archive = true) => {
    try {
      await api.post(`/contracts/tasks/${taskId}/${archive ? 'archive' : 'unarchive'}`);
      toast.success(archive ? 'Conversation archivée' : 'Conversation restaurée');
      fetchConversations();
      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };
  
  // Audio functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setShowAudioOptions(true);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      
      const res = await api.post('/audio/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewMessage(prev => prev + (prev ? ' ' : '') + (res.data.text || ''));
      setAudioBlob(null);
      setShowAudioOptions(false);
      toast.success('Transcription terminée');
    } catch (error) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const sendAudioMessage = async () => {
    if (!audioBlob || !selectedTask) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio-message.webm');
      formData.append('message', '🎤 Message vocal');
      formData.append('is_deliverable', 'false');
      
      await api.post(`/contracts/tasks/${selectedTask.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAudioBlob(null);
      setShowAudioOptions(false);
      fetchMessages(selectedTask.id);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchQuery || 
      conv.task_title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArchived = showArchived ? conv.archived : !conv.archived;
    return matchesSearch && matchesArchived;
  });
  
  // Get status badge
  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'En attente', className: 'bg-slate-100 text-slate-700' },
      in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-700' },
      completed: { label: 'Terminé', className: 'bg-emerald-100 text-emerald-700' },
      blocked: { label: 'Bloqué', className: 'bg-red-100 text-red-700' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.className}>{c.label}</Badge>;
  };
  
  // Render message
  const renderMessage = (msg) => {
    const isOwn = msg.user_id === user?.id;
    const isDeliverable = msg.is_deliverable || msg.type === 'file' && msg.is_deliverable;
    const isVersionResponse = msg.type === 'version_response';
    const hasFile = msg.file_url;
    
    const getFullUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http')) return url;
      return `${BACKEND_URL}${url}`;
    };
    
    return (
      <div key={msg.id} className={cn("flex mb-3", isOwn ? "justify-end" : "justify-start")}>
        <div className={cn(
          "max-w-[75%] rounded-2xl px-4 py-2",
          isDeliverable && "border-2 border-amber-300 bg-amber-50",
          isVersionResponse && (msg.content?.includes('✅') ? "border-2 border-emerald-300 bg-emerald-50" : "border-2 border-orange-300 bg-orange-50"),
          !isDeliverable && !isVersionResponse && (
            isOwn 
              ? "bg-teal-400 text-white rounded-br-sm" 
              : "bg-slate-100 text-slate-800 rounded-bl-sm"
          )
        )}>
          {!isOwn && (
            <p className={cn(
              "text-xs font-medium mb-1",
              isDeliverable || isVersionResponse ? "text-slate-600" : "opacity-70"
            )}>
              {msg.user_name}
            </p>
          )}
          
          {/* Deliverable badge */}
          {isDeliverable && msg.version && (
            <div className="mb-2">
              <Badge className="bg-amber-500 text-white">
                <Package className="w-3 h-3 mr-1" />
                Livrable v{msg.version.version_number}
              </Badge>
              {msg.version.status === 'pending_validation' && isClient && (
                <Button
                  size="sm"
                  className="ml-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => {
                    setSelectedVersion(msg.version);
                    setShowValidationModal(true);
                  }}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Valider
                </Button>
              )}
            </div>
          )}
          
          {/* File attachment */}
          {hasFile && (
            <div className="mb-2">
              {/* Image preview */}
              {msg.file_url && /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(msg.file_name || msg.file_url) ? (
                <div className="space-y-2">
                  <a 
                    href={getFullUrl(msg.file_url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img 
                      src={getFullUrl(msg.file_url)} 
                      alt={msg.file_name || 'Image'}
                      className="max-w-full max-h-64 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden items-center gap-2 p-2 rounded-lg bg-white/70 border border-slate-200">
                      <FileText className="w-5 h-5 text-slate-600" />
                      <span className="text-sm truncate flex-1 text-slate-700">{msg.file_name || 'Image'}</span>
                    </div>
                  </a>
                  <p className="text-xs text-slate-500">{msg.file_name}</p>
                </div>
              ) : /\.(mp3|wav|ogg|webm|m4a)$/i.test(msg.file_name || msg.file_url || '') ? (
                <div className="p-2 rounded-lg bg-white/70 border border-slate-200">
                  <audio 
                    controls 
                    src={getFullUrl(msg.file_url)}
                    className="w-full max-w-[250px]"
                  />
                  <p className="text-xs text-slate-500 mt-1">{msg.file_name || 'Audio'}</p>
                </div>
              ) : (
                <a 
                  href={getFullUrl(msg.file_url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/70 border border-slate-200 hover:bg-white transition-colors"
                >
                  <FileText className="w-5 h-5 text-slate-600" />
                  <span className="text-sm truncate flex-1 text-slate-700">
                    {msg.file_name || 'Fichier'}
                  </span>
                  <Download className="w-4 h-4 text-slate-500" />
                </a>
              )}
            </div>
          )}
          
          {/* Message content */}
          {msg.content && (
            <p className={cn(
              "text-sm whitespace-pre-wrap",
              (isDeliverable || isVersionResponse) && "text-slate-800"
            )}>
              {msg.content}
            </p>
          )}
          
          <div className={cn(
            "flex items-center justify-end mt-1 text-[10px]",
            (isDeliverable || isVersionResponse) ? "text-slate-400" : (isOwn ? "text-white/70" : "text-slate-400")
          )}>
            {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <MainLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout user={user}>
      <div className="flex flex-col h-[calc(100vh-120px)] md:h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-2 md:mb-4 px-2 md:px-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 md:w-5 md:h-5 text-teal-600 shrink-0" />
              <h1 className="text-lg md:text-xl font-bold truncate" style={{ color: theme.colors?.primary }}>
                {contract?.name || 'Contrat'}
              </h1>
            </div>
            <p className="text-xs md:text-sm text-slate-500 truncate">
              {contract?.client_name} · {filteredConversations.length} tâche(s)
              {contract?.hours_included > 0 && (
                <span className="ml-2">
                  · {contract?.hours_used?.toFixed(1) || 0}h / {contract?.hours_included}h
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex gap-2 md:gap-4 overflow-hidden px-2 md:px-0">
          {/* Conversations list */}
          <div className={cn(
            "w-full md:w-80 flex flex-col bg-white rounded-xl border shadow-sm",
            mobileShowChat && "hidden md:flex"
          )}>
            {/* Search */}
            <div className="p-2 md:p-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher..."
                  className="pl-10 h-9 md:h-10 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={showArchived ? "default" : "outline"}
                  size="sm"
                  className="flex-1 h-8 text-xs md:text-sm"
                  onClick={() => setShowArchived(!showArchived)}
                >
                  <Archive className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  {showArchived ? 'Archives' : 'Actives'}
                </Button>
              </div>
            </div>
            
            {/* Task list */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Wrench className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune tâche de maintenance</p>
                  </div>
                ) : (
                  filteredConversations.map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => {
                        setSelectedTask(conv);
                        setMobileShowChat(true);
                      }}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-colors",
                        selectedTask?.id === conv.id 
                          ? "bg-teal-50 border border-teal-200" 
                          : "hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {conv.task_title}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {contract?.name}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {conv.unread_count > 0 && (
                            <Badge className="bg-teal-500 text-white text-[10px] px-1.5">
                              {conv.unread_count}
                            </Badge>
                          )}
                          {conv.has_pending_validation && (
                            <Badge className="bg-amber-500 text-white text-[10px]">
                              À valider
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {conv.last_message && (
                        <p className="text-xs text-slate-400 mt-1 truncate">
                          {conv.last_message.sender_name}: {conv.last_message.content}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2">
                        {getStatusBadge(conv.status)}
                        {conv.current_version > 0 && (
                          <Badge variant="outline" className="text-[10px]">
                            v{conv.current_version}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
          
          {/* Chat area */}
          <div className={cn(
            "flex-1 flex flex-col bg-white rounded-xl border shadow-sm min-w-0",
            !mobileShowChat && "hidden md:flex"
          )}>
            {selectedTask ? (
              <>
                {/* Task header */}
                <div className="p-2 md:p-4 border-b flex items-center justify-between gap-2 bg-gradient-to-r from-teal-50 to-white">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden shrink-0 h-8 w-8"
                      onClick={() => setMobileShowChat(false)}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold text-sm md:text-base truncate">{selectedTask.task_title}</h2>
                      <p className="text-xs md:text-sm text-slate-500 truncate">{contract?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 shrink-0">
                    <div className="hidden sm:block">{getStatusBadge(selectedTask.status)}</div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleArchive(selectedTask.id, !selectedTask.archived)}
                      title={selectedTask.archived ? "Restaurer" : "Archiver"}
                    >
                      {selectedTask.archived ? (
                        <ArchiveRestore className="w-4 h-4" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Messages */}
                <ScrollArea className="flex-1 p-2 md:p-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <MessageSquare className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Démarrez la conversation</p>
                    </div>
                  ) : (
                    messages.map(msg => renderMessage(msg))
                  )}
                  <div ref={messagesEndRef} />
                </ScrollArea>
                
                {/* Audio preview */}
                {showAudioOptions && audioBlob && (
                  <div className="px-2 md:px-4 py-2 md:py-3 bg-teal-50 border-t border-teal-200 flex flex-wrap items-center gap-2">
                    <Mic className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                    <span className="text-xs md:text-sm text-teal-700 flex-1">Message vocal</span>
                    <div className="flex gap-1 md:gap-2">
                      <Button size="sm" variant="outline" onClick={sendAudioMessage} disabled={sending} className="h-7 md:h-8 text-xs md:text-sm px-2 md:px-3">
                        <Send className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        <span className="hidden sm:inline">Envoyer</span>
                      </Button>
                      <Button size="sm" onClick={transcribeAudio} disabled={isTranscribing} className="bg-teal-500 hover:bg-teal-600 h-7 md:h-8 text-xs md:text-sm px-2 md:px-3">
                        {isTranscribing ? <Loader2 className="w-3 h-3 md:w-4 md:h-4 animate-spin" /> : <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1" />}
                        <span className="hidden sm:inline">Transcrire</span>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setAudioBlob(null); setShowAudioOptions(false); }} className="h-7 w-7 md:h-8 md:w-8">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Selected file preview */}
                {selectedFile && (
                  <div className="px-2 md:px-4 py-2 bg-teal-50 border-t border-teal-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-600 shrink-0" />
                    <span className="text-xs md:text-sm text-teal-700 truncate flex-1">{selectedFile.name}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setSelectedFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {/* Input area - Add extra padding on mobile for client FAB */}
                <div className={cn(
                  "p-2 md:p-4 border-t flex items-center gap-1 md:gap-2",
                  isClient && "pb-20 md:pb-4"
                )}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-teal-600 hover:bg-teal-100 h-8 w-8 md:h-10 md:w-10 shrink-0"
                  >
                    <Paperclip className="w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={cn(
                      "h-8 w-8 md:h-10 md:w-10 shrink-0",
                      isRecording 
                        ? "text-red-500 hover:bg-red-100 animate-pulse" 
                        : "text-teal-600 hover:bg-teal-100"
                    )}
                  >
                    <Mic className="w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                  {!isClient && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowDeliverableModal(true)}
                      className="text-amber-600 hover:bg-amber-100 h-8 w-8 md:h-10 md:w-10 shrink-0"
                      title="Envoyer un livrable"
                    >
                      <Package className="w-4 h-4 md:w-5 md:h-5" />
                    </Button>
                  )}
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={isRecording ? "Enregistrement..." : "Message..."}
                    className="flex-1 min-w-0 h-8 md:h-10 text-sm"
                    disabled={isRecording}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!newMessage.trim() && !selectedFile) || sending || isRecording}
                    size="icon"
                    className="bg-teal-400 hover:bg-teal-500 h-8 w-8 md:h-10 md:w-10 shrink-0"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400 p-4">
                <div className="text-center">
                  <Wrench className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-medium text-sm md:text-base">Sélectionnez une tâche</p>
                  <p className="text-xs md:text-sm">pour voir sa conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Deliverable Modal */}
      <Dialog open={showDeliverableModal} onOpenChange={setShowDeliverableModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Envoyer un livrable
            </DialogTitle>
            <DialogDescription>
              Le client pourra valider ou demander des modifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Fichier à valider *</Label>
              <div className="mt-1">
                <input
                  type="file"
                  ref={deliverableInputRef}
                  onChange={(e) => setDeliverableFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                {deliverableFile ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <FileText className="w-5 h-5 text-amber-600" />
                    <span className="flex-1 truncate text-sm">{deliverableFile.name}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setDeliverableFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full border-dashed border-amber-400 text-amber-700"
                    onClick={() => deliverableInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Joindre un fichier (obligatoire)
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={deliverableComment}
                onChange={(e) => setDeliverableComment(e.target.value)}
                placeholder="Description du livrable..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliverableModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendDeliverable}
              disabled={!deliverableFile || sendingDeliverable}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {sendingDeliverable ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Validation Modal */}
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Valider le livrable</DialogTitle>
            <DialogDescription>
              Version {selectedVersion?.version_number} - {selectedVersion?.file_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedVersion?.file_url && (
              <div className="space-y-2">
                {/* Image preview for validation */}
                {/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(selectedVersion.file_name || selectedVersion.file_url) ? (
                  <>
                    <a 
                      href={`${BACKEND_URL}${selectedVersion.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={`${BACKEND_URL}${selectedVersion.file_url}`} 
                        alt={selectedVersion.file_name || 'Livrable'}
                        className="max-w-full max-h-48 rounded-lg border border-slate-200 shadow-sm mx-auto"
                      />
                    </a>
                    <p className="text-xs text-slate-500 text-center">{selectedVersion.file_name}</p>
                  </>
                ) : (
                  <a 
                    href={`${BACKEND_URL}${selectedVersion.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                  >
                    <FileText className="w-5 h-5 text-slate-600" />
                    <span className="flex-1 text-sm">{selectedVersion.file_name}</span>
                    <Download className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={validationResponse === 'accepted' ? 'default' : 'outline'}
                className={cn(
                  validationResponse === 'accepted' && "bg-emerald-500 hover:bg-emerald-600"
                )}
                onClick={() => setValidationResponse('accepted')}
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                Valider
              </Button>
              <Button
                variant={validationResponse === 'rejected' ? 'default' : 'outline'}
                className={cn(
                  validationResponse === 'rejected' && "bg-orange-500 hover:bg-orange-600"
                )}
                onClick={() => setValidationResponse('rejected')}
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Modifications
              </Button>
            </div>
            
            {validationResponse === 'rejected' && (
              <div>
                <Label>Vos remarques</Label>
                <Textarea
                  value={validationNotes}
                  onChange={(e) => setValidationNotes(e.target.value)}
                  placeholder="Décrivez les modifications souhaitées..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleValidateVersion}
              disabled={!validationResponse || validating}
              className={validationResponse === 'accepted' ? "bg-emerald-500 hover:bg-emerald-600" : "bg-orange-500 hover:bg-orange-600"}
            >
              {validating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ContractChatPage;
